import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { createRegistryResolver, tierSource } from './resolveCore';
import type { RegistryResolver, ResolverFactoryOptions } from './types';

export interface EvidenceResolverInput extends ResolverFactoryOptions {
  sed?: SiteEvidenceDocument;
  docOverrides?: Record<string, { value: unknown; docId: string; docName?: string }>;
  llmEvidence?: Record<string, unknown>;
}

export function createEvidenceResolver(opts: EvidenceResolverInput): RegistryResolver {
  const fieldSources: Record<string, ReturnType<typeof tierSource>[]> = {};

  if (opts.sed) {
    for (const [sectionId, section] of Object.entries(opts.sed)) {
      if (!section || typeof section !== 'object') continue;
      const slot = section as {
        data?: Record<string, unknown>;
        source?: string;
        provenance?: string;
        confidence?: number;
      };
      if (!slot.data) continue;
      for (const [fieldKey, value] of Object.entries(slot.data)) {
        if (value === undefined || value === null) continue;
        const path = `${sectionId}.${fieldKey}`;
        fieldSources[path] = [
          tierSource(1, value, {
            kind: 'provider',
            ref: `evidence:${path}`,
            label: slot.source ?? sectionId,
          }),
        ];
      }
    }
  }

  for (const [path, doc] of Object.entries(opts.docOverrides ?? {})) {
    const sources = fieldSources[path] ?? [];
    sources.unshift(
      tierSource(1, doc.value, {
        kind: 'document',
        ref: `doc:${doc.docId}`,
        label: doc.docName ?? 'Uploaded document',
      }),
    );
    fieldSources[path] = sources;
  }

  for (const [path, value] of Object.entries(opts.llmEvidence ?? {})) {
    if (value === undefined || value === null) continue;
    const sources = fieldSources[path] ?? [];
    sources.push(
      tierSource(3, value, {
        kind: 'llm-inference',
        ref: `llm:evidence:${path}`,
        label: 'LLM evidence inference',
      }),
    );
    fieldSources[path] = sources;
  }

  return createRegistryResolver({
    registryId: 'evidence',
    siteId: opts.siteId,
    fieldSources,
    now: opts.now,
  });
}
